<template>
  <div class="nutrition-reminders">
    <div class="reminders-header">
      <h3>Nutrition Reminders</h3>
      <button class="add-btn" @click="showAddForm = true">
        + Add Reminder
      </button>
    </div>

    <div v-if="activeAlerts.length" class="active-alerts">
      <div
        v-for="alert in activeAlerts"
        :key="alert.id"
        class="alert-banner"
        :class="alert.reminder_type"
      >
        <div class="alert-content">
          <span class="alert-icon">{{ getTypeIcon(alert.reminder_type) }}</span>
          <div class="alert-info">
            <span class="alert-title">{{ getTypeLabel(alert.reminder_type) }}</span>
            <span class="alert-message">{{ alert.message || getDefaultMessage(alert.reminder_type) }}</span>
          </div>
        </div>
        <button class="dismiss-btn" @click="dismissAlert(alert.id)">
          &#x2715;
        </button>
      </div>
    </div>

    <div v-if="showAddForm" class="add-form-overlay" @click.self="showAddForm = false">
      <div class="add-form">
        <h4>{{ editingReminder ? 'Edit Reminder' : 'New Reminder' }}</h4>

        <div class="form-group">
          <label>Type</label>
          <div class="type-options">
            <button
              v-for="type in reminderTypes"
              :key="type.value"
              class="type-btn"
              :class="{ active: newReminder.reminder_type === type.value }"
              @click="newReminder.reminder_type = type.value"
            >
              <span class="type-icon" :style="{ backgroundColor: type.color }">
                {{ type.icon }}
              </span>
              <span class="type-label">{{ type.label }}</span>
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Time</label>
          <input
            type="time"
            v-model="newReminder.reminder_time"
            class="time-input"
          />
        </div>

        <div class="form-group">
          <label>Active Days</label>
          <div class="days-selector">
            <button
              v-for="day in daysOfWeek"
              :key="day.value"
              class="day-btn"
              :class="{ active: newReminder.days_active.includes(day.value) }"
              @click="toggleDay(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Custom Message (optional)</label>
          <input
            type="text"
            v-model="newReminder.message"
            placeholder="e.g., Time to drink water!"
            class="message-input"
          />
        </div>

        <div class="form-actions">
          <button class="cancel-btn" @click="cancelForm">Cancel</button>
          <button
            class="save-btn"
            @click="saveReminder"
            :disabled="!isValidReminder || saving"
          >
            {{ saving ? 'Saving...' : 'Save Reminder' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else-if="!reminders.length" class="empty-state">
      <p>No reminders set</p>
      <p class="hint">Add reminders to stay on track with your nutrition</p>
    </div>

    <div v-else class="reminders-list">
      <div
        v-for="reminder in reminders"
        :key="reminder.id"
        class="reminder-card"
        :class="{ disabled: !reminder.is_enabled }"
      >
        <div class="reminder-main">
          <span
            class="reminder-icon"
            :style="{ backgroundColor: getTypeColor(reminder.reminder_type) }"
          >
            {{ getTypeIcon(reminder.reminder_type) }}
          </span>
          <div class="reminder-info">
            <span class="reminder-type">{{ getTypeLabel(reminder.reminder_type) }}</span>
            <span class="reminder-time">{{ formatTime(reminder.reminder_time) }}</span>
            <div class="reminder-days">
              <span
                v-for="day in daysOfWeek"
                :key="day.value"
                class="day-indicator"
                :class="{ active: reminder.days_active && reminder.days_active.includes(day.value) }"
              >
                {{ day.label }}
              </span>
            </div>
          </div>
        </div>

        <div class="reminder-actions">
          <label class="toggle-switch">
            <input
              type="checkbox"
              :checked="reminder.is_enabled"
              @change="toggleReminder(reminder)"
            />
            <span class="toggle-slider"></span>
          </label>
          <button class="edit-btn" @click="editReminder(reminder)">
            &#9998;
          </button>
          <button class="delete-btn" @click="deleteReminder(reminder.id)">
            &#128465;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Nutrition from "@/services/nutrition-service";

export default {
  name: "NutritionReminders",

  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      reminders: [],
      activeAlerts: [],
      dismissedAlerts: [],
      showAddForm: false,
      editingReminder: null,
      newReminder: {
        reminder_type: "hydration",
        reminder_time: "10:00",
        days_active: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        is_enabled: true,
        message: "",
      },
      reminderTypes: [],
      daysOfWeek: [
        { value: "mon", label: "M" },
        { value: "tue", label: "T" },
        { value: "wed", label: "W" },
        { value: "thu", label: "T" },
        { value: "fri", label: "F" },
        { value: "sat", label: "S" },
        { value: "sun", label: "S" },
      ],
      checkInterval: null,
    };
  },

  computed: {
    isValidReminder() {
      return (
        this.newReminder.reminder_type &&
        this.newReminder.reminder_time &&
        this.newReminder.days_active.length > 0
      );
    },
  },

  async mounted() {
    this.reminderTypes = Nutrition.getReminderTypes();
    await this.loadReminders();
    await this.checkActiveReminders();

    // Check for active reminders every 5 minutes
    this.checkInterval = setInterval(() => {
      this.checkActiveReminders();
    }, 5 * 60 * 1000);
  },

  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },

  methods: {
    async loadReminders() {
      this.loading = true;
      try {
        const response = await Nutrition.getReminders(this.userId);
        if (response.data && response.data.success) {
          this.reminders = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading reminders:", error);
      } finally {
        this.loading = false;
      }
    },

    async checkActiveReminders() {
      try {
        const response = await Nutrition.checkActiveReminders(this.userId);
        if (response.data && response.data.success) {
          const alerts = response.data.data || [];
          // Filter out dismissed alerts
          this.activeAlerts = alerts.filter(
            (a) => !this.dismissedAlerts.includes(a.id)
          );
        }
      } catch (error) {
        console.error("Error checking active reminders:", error);
      }
    },

    async saveReminder() {
      if (!this.isValidReminder || this.saving) return;

      this.saving = true;
      try {
        const reminderData = {
          user_id: this.userId,
          ...this.newReminder,
        };

        if (this.editingReminder) {
          reminderData.id = this.editingReminder.id;
        }

        const response = await Nutrition.saveReminder(reminderData);
        if (response.data && response.data.success) {
          await this.loadReminders();
          this.cancelForm();
        }
      } catch (error) {
        console.error("Error saving reminder:", error);
      } finally {
        this.saving = false;
      }
    },

    async toggleReminder(reminder) {
      try {
        const response = await Nutrition.saveReminder({
          id: reminder.id,
          user_id: this.userId,
          reminder_type: reminder.reminder_type,
          reminder_time: reminder.reminder_time,
          days_active: reminder.days_active,
          is_enabled: reminder.is_enabled ? 0 : 1,
          message: reminder.message,
        });

        if (response.data && response.data.success) {
          reminder.is_enabled = !reminder.is_enabled;
        }
      } catch (error) {
        console.error("Error toggling reminder:", error);
      }
    },

    async deleteReminder(reminderId) {
      if (!confirm("Delete this reminder?")) return;

      try {
        const response = await Nutrition.deleteReminder(reminderId, this.userId);
        if (response.data && response.data.success) {
          this.reminders = this.reminders.filter((r) => r.id !== reminderId);
        }
      } catch (error) {
        console.error("Error deleting reminder:", error);
      }
    },

    editReminder(reminder) {
      this.editingReminder = reminder;
      this.newReminder = {
        reminder_type: reminder.reminder_type,
        reminder_time: reminder.reminder_time,
        days_active: [...(reminder.days_active || [])],
        is_enabled: reminder.is_enabled,
        message: reminder.message || "",
      };
      this.showAddForm = true;
    },

    cancelForm() {
      this.showAddForm = false;
      this.editingReminder = null;
      this.newReminder = {
        reminder_type: "hydration",
        reminder_time: "10:00",
        days_active: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
        is_enabled: true,
        message: "",
      };
    },

    toggleDay(day) {
      const index = this.newReminder.days_active.indexOf(day);
      if (index > -1) {
        this.newReminder.days_active.splice(index, 1);
      } else {
        this.newReminder.days_active.push(day);
      }
    },

    dismissAlert(alertId) {
      this.dismissedAlerts.push(alertId);
      this.activeAlerts = this.activeAlerts.filter((a) => a.id !== alertId);
    },

    formatTime(time) {
      if (!time) return "";
      const [hours, mins] = time.split(":");
      const h = parseInt(hours, 10);
      const period = h >= 12 ? "PM" : "AM";
      const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
      return `${displayHour}:${mins} ${period}`;
    },

    getTypeLabel(type) {
      const found = this.reminderTypes.find((t) => t.value === type);
      return found ? found.label : type;
    },

    getTypeIcon(type) {
      const found = this.reminderTypes.find((t) => t.value === type);
      return found ? found.icon : "N";
    },

    getTypeColor(type) {
      const found = this.reminderTypes.find((t) => t.value === type);
      return found ? found.color : "#6c757d";
    },

    getDefaultMessage(type) {
      const messages = {
        hydration: "Time to drink some water!",
        pre_match_meal: "Eat your pre-match meal now",
        post_match_meal: "Time for recovery nutrition",
        general: "Nutrition reminder",
      };
      return messages[type] || "Nutrition reminder";
    },
  },
};
</script>

<style lang="scss" scoped>
.nutrition-reminders {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.reminders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.add-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  }
}

.active-alerts {
  margin-bottom: 20px;
}

.alert-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  animation: slideIn 0.3s ease;

  &.hydration {
    background: #e7f7fa;
    border-left: 4px solid #17a2b8;
  }

  &.pre_match_meal {
    background: #f8d7da;
    border-left: 4px solid #dc3545;
  }

  &.post_match_meal {
    background: #cce5ff;
    border-left: 4px solid #007bff;
  }

  &.general {
    background: #e9ecef;
    border-left: 4px solid #6c757d;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.alert-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
}

.alert-info {
  display: flex;
  flex-direction: column;
}

.alert-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
}

.alert-message {
  font-size: 0.8rem;
  color: #666;
}

.dismiss-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: #666;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}

.add-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.add-form {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;

  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin: 0 0 20px 0;
  }
}

.form-group {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
  }
}

.type-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.type-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #007bff;
  }

  &.active {
    border-color: #007bff;
    background: #e7f0ff;
  }
}

.type-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: white;
}

.type-label {
  font-size: 0.8rem;
  color: #333;
}

.time-input,
.message-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: #007bff;
  }
}

.days-selector {
  display: flex;
  gap: 6px;
}

.day-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    border-color: #007bff;
  }

  &.active {
    background: #007bff;
    border-color: #007bff;
    color: white;
  }
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
  }
}

.save-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 30px;
  color: #666;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f0f0f0;
  border-top-color: #007bff;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.hint {
  font-size: 0.8rem;
  color: #888;
  margin-top: 8px;
}

.reminders-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reminder-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.2s;

  &.disabled {
    opacity: 0.5;
  }
}

.reminder-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.reminder-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: white;
}

.reminder-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.reminder-type {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
}

.reminder-time {
  font-size: 0.8rem;
  color: #666;
}

.reminder-days {
  display: flex;
  gap: 3px;
  margin-top: 4px;
}

.day-indicator {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 600;
  color: #999;

  &.active {
    background: #007bff;
    color: white;
  }
}

.reminder-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
}

input:checked + .toggle-slider {
  background-color: #28a745;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.edit-btn,
.delete-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
  }
}

.delete-btn:hover {
  background: #f8d7da;
  color: #dc3545;
}
</style>
